/**
 * Grand Quack Auto — background music synthesizer.
 *
 * GTA San Andreas-inspired G-funk groove. All sounds generated via Web Audio
 * API — no files, zero bundle cost. Extracted from audio.ts for 200-line cap.
 */

import { getCtx, getMusicDestination } from './audioMixer';

let musicStarted = false;

// Module-scope refs so we can stop them on death.
let subOsc: OscillatorNode | null = null;
let bassOsc: OscillatorNode | null = null;
let stabOsc: OscillatorNode | null = null;
let bassInterval: ReturnType<typeof setInterval> | null = null;
let hatInterval: ReturnType<typeof setInterval> | null = null;
let stabInterval: ReturnType<typeof setInterval> | null = null;

/** Start background music on first user interaction. */
export function startBackgroundMusic(): void {
  if (musicStarted) return;
  musicStarted = true;

  const c = getCtx();
  if (c.state === 'suspended') c.resume();
  const BPM = 90;
  const beatSec = 60 / BPM;
  const sixteenth = beatSec / 4;

  // Sub-bass (deep sine, San Andreas low-end).
  subOsc = c.createOscillator();
  subOsc.type = 'sine';
  subOsc.frequency.value = 55; // A1
  const subGain = c.createGain();
  subGain.gain.value = 0.08;
  subOsc.connect(subGain).connect(getMusicDestination());
  subOsc.start();

  // Bass pattern — G-funk bounce (A, C, D, C, A, E, D, C).
  const bassNotes = [110, 130.81, 146.83, 130.81, 110, 164.81, 146.83, 130.81];
  bassOsc = c.createOscillator();
  bassOsc.type = 'sawtooth';
  bassOsc.frequency.value = bassNotes[0]!;
  const bassFilter = c.createBiquadFilter();
  bassFilter.type = 'lowpass';
  bassFilter.frequency.value = 400;
  bassFilter.Q.value = 3;
  const bassGain = c.createGain();
  bassGain.gain.value = 0.05;
  bassOsc.connect(bassFilter).connect(bassGain).connect(getMusicDestination());
  bassOsc.start();

  let bassIdx = 0;
  const _subOsc = subOsc; // capture for closure
  const _bassOsc = bassOsc;
  bassInterval = setInterval(() => {
    bassIdx = (bassIdx + 1) % bassNotes.length;
    _bassOsc.frequency.setTargetAtTime(bassNotes[bassIdx]!, c.currentTime, 0.02);
    _subOsc.frequency.setTargetAtTime(bassNotes[bassIdx]! / 2, c.currentTime, 0.05);
  }, beatSec * 500);

  // Hi-hat (noise bursts on 16ths).
  function playHat(): void {
    const buf = c.createBuffer(1, c.sampleRate * 0.03, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1);
    const src = c.createBufferSource();
    src.buffer = buf;
    const hpf = c.createBiquadFilter();
    hpf.type = 'highpass';
    hpf.frequency.value = 8000;
    const g = c.createGain();
    g.gain.value = 0.03;
    src.connect(hpf).connect(g).connect(getMusicDestination());
    src.start();
  }

  let hatStep = 0;
  hatInterval = setInterval(() => {
    hatStep++;
    if (hatStep % 4 !== 3) playHat();
  }, sixteenth * 1000);

  // Synth stab (G-funk whine on offbeats).
  stabOsc = c.createOscillator();
  stabOsc.type = 'square';
  stabOsc.frequency.value = 440;
  const stabFilter = c.createBiquadFilter();
  stabFilter.type = 'bandpass';
  stabFilter.frequency.value = 1200;
  stabFilter.Q.value = 5;
  const stabGain = c.createGain();
  stabGain.gain.value = 0;
  stabOsc.connect(stabFilter).connect(stabGain).connect(getMusicDestination());
  stabOsc.start();

  const stabNotes = [440, 523.25, 587.33, 523.25];
  let stabIdx = 0;
  let stabBeat = 0;
  const _stabOsc = stabOsc;
  stabInterval = setInterval(() => {
    stabBeat++;
    if (stabBeat % 2 === 0) {
      stabIdx = (stabIdx + 1) % stabNotes.length;
      _stabOsc.frequency.setTargetAtTime(stabNotes[stabIdx]!, c.currentTime, 0.01);
      stabGain.gain.setTargetAtTime(0.02, c.currentTime, 0.01);
      stabGain.gain.setTargetAtTime(0, c.currentTime + beatSec * 0.3, 0.05);
    }
  }, beatSec * 500);
}

function stopOsc(osc: OscillatorNode | null): void {
  if (!osc) return;
  try { osc.stop(); } catch { /* already stopped */ }
  osc.disconnect();
}

/** Stop all background music. Called by stopAllAudio on death/end. */
export function stopBackgroundMusic(): void {
  if (bassInterval) { clearInterval(bassInterval); bassInterval = null; }
  if (hatInterval) { clearInterval(hatInterval); hatInterval = null; }
  if (stabInterval) { clearInterval(stabInterval); stabInterval = null; }
  stopOsc(subOsc); subOsc = null;
  stopOsc(bassOsc); bassOsc = null;
  stopOsc(stabOsc); stabOsc = null;
  musicStarted = false;
}
